import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixQuickpagesV1PublishedPage from './quickpages-v1-published-page-published-pages.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** A PublishedPage of QuickPages */
export interface PublishedPage {
  /**
   * PublishedPage ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the PublishedPage is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the PublishedPage.
   *
   * Ignored when creating a PublishedPage.
   * @readonly
   */
  revision?: string | null;
  /**
   * QuickPage page ID.
   * @format GUID
   * @immutable
   */
  pageId?: string;
  /**
   * QuickPage page ID.
   * @format GUID
   * @immutable
   */
  contentId?: string;
  /**
   * Page slug used in the URL.
   * @minLength 3
   * @maxLength 100
   */
  slug?: string;
  /**
   * Date and time the PublishedPage was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the PublishedPage was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface GetPublishedPageRequest {
  /**
   * ID of the PublishedPage to retrieve.
   * @format GUID
   */
  publishedPageId: string;
}

export interface GetPublishedPageResponse {
  /** The requested PublishedPage. */
  publishedPage?: PublishedPage;
}

export interface GetPublishedPageBySlugRequest {
  /**
   * ID of the PublishedPage to retrieve.
   * @minLength 3
   * @maxLength 100
   */
  slug: string;
}

export interface GetPublishedPageBySlugResponse {
  /** The requested PublishedPage. */
  publishedPage?: PublishedPage;
}

export interface GetPublishedPageByPageIdRequest {
  /**
   * ID of the PublishedPage to retrieve.
   * @format GUID
   */
  pageId: string;
}

export interface GetPublishedPageByPageIdResponse {
  /** The requested PublishedPage. */
  publishedPage?: PublishedPage;
}

export interface QueryPublishedPagesRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPublishedPagesResponse {
  /** List of PublishedPages. */
  publishedPages?: PublishedPage[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Retrieves a PublishedPage.
 * @param publishedPageId - ID of the PublishedPage to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField publishedPageId
 * @permissionId QUICK_PAGES.PUBLISHED_PAGE_READ
 * @returns The requested PublishedPage.
 * @fqn wix.quickpages.publishedpages.v1.PublishedPages.GetPublishedPage
 */
export async function getPublishedPage(
  publishedPageId: string
): Promise<
  NonNullablePaths<PublishedPage, `pageId` | `contentId` | `slug`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    publishedPageId: publishedPageId,
  });

  const reqOpts =
    ambassadorWixQuickpagesV1PublishedPage.getPublishedPage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.publishedPage!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { publishedPageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['publishedPageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a PublishedPage by slug.
 * @param slug - ID of the PublishedPage to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField slug
 * @permissionId QUICK_PAGES.PUBLISHED_PAGE_READ
 * @fqn wix.quickpages.publishedpages.v1.PublishedPages.GetPublishedPageBySlug
 */
export async function getPublishedPageBySlug(
  slug: string
): Promise<
  NonNullablePaths<
    GetPublishedPageBySlugResponse,
    `publishedPage.pageId` | `publishedPage.contentId` | `publishedPage.slug`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ slug: slug });

  const reqOpts =
    ambassadorWixQuickpagesV1PublishedPage.getPublishedPageBySlug(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { slug: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['slug']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a PublishedPage by slug.
 * @param pageId - ID of the PublishedPage to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField pageId
 * @permissionId QUICK_PAGES.PUBLISHED_PAGE_READ
 * @fqn wix.quickpages.publishedpages.v1.PublishedPages.GetPublishedPageByPageId
 */
export async function getPublishedPageByPageId(
  pageId: string
): Promise<
  NonNullablePaths<
    GetPublishedPageByPageIdResponse,
    `publishedPage.pageId` | `publishedPage.contentId` | `publishedPage.slug`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ pageId: pageId });

  const reqOpts =
    ambassadorWixQuickpagesV1PublishedPage.getPublishedPageByPageId(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pageId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pageId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of PublishedPages, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 PublishedPages can be returned per request.
 *
 * To learn how to query PublishedPages, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @internal
 * @documentationMaturity preview
 * @permissionId QUICK_PAGES.PUBLISHED_PAGE_READ
 * @fqn wix.quickpages.publishedpages.v1.PublishedPages.QueryPublishedPages
 */
export function queryPublishedPages(): PublishedPagesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PublishedPage,
    'CURSOR',
    QueryPublishedPagesRequest,
    QueryPublishedPagesResponse
  >({
    func: async (payload: QueryPublishedPagesRequest) => {
      const reqOpts =
        ambassadorWixQuickpagesV1PublishedPage.queryPublishedPages(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPublishedPagesRequest['query']) => {
      const args = [query, {}] as [QueryPublishedPagesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPublishedPagesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.publishedPages,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PublishedPagesQueryResult extends QueryCursorResult {
  items: PublishedPage[];
  query: PublishedPagesQueryBuilder;
  next: () => Promise<PublishedPagesQueryResult>;
  prev: () => Promise<PublishedPagesQueryResult>;
}

export interface PublishedPagesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id',
    value: string
  ) => PublishedPagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (propertyName: '_id', value: any[]) => PublishedPagesQueryBuilder;
  /** @documentationMaturity preview */
  in: (propertyName: '_id', value: any) => PublishedPagesQueryBuilder;
  /** @documentationMaturity preview */
  exists: (propertyName: '_id', value: boolean) => PublishedPagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (...propertyNames: Array<'_id'>) => PublishedPagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (...propertyNames: Array<'_id'>) => PublishedPagesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => PublishedPagesQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => PublishedPagesQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<PublishedPagesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.quickpages.publishedpages.v1.PublishedPages.QueryPublishedPages
 * @requiredField query
 */
export async function typedQueryPublishedPages(
  query: PublishedPageQuery
): Promise<
  NonNullablePaths<
    QueryPublishedPagesResponse,
    | `publishedPages`
    | `publishedPages.${number}.pageId`
    | `publishedPages.${number}.contentId`
    | `publishedPages.${number}.slug`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixQuickpagesV1PublishedPage.queryPublishedPages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishedPageQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  PublishedPage,
  PublishedPageQuerySpec
>;
export type PublishedPageQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      PublishedPage,
      PublishedPageQuerySpec,
      PublishedPageQuery
    >(),
  },
};
